import { useEffect, useState } from 'react'
import { Wrench, Coffee, PauseCircle, AlertTriangle, Save, Power, Clock } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import PageHeader from '@/components/PageHeader'
import { maintenanceAPI } from '@/lib/api'
import { useAuth } from '@/hooks/useAuth'
import usePageTitle from '@/lib/usePageTitle'

/* Onderhouds-modus beheer.
   Toggle + preset + title/message + optioneel ETA.
   Deelnemers zien binnen 60 s de wijziging via de MaintenanceGate. */

const PRESETS = [
  { key: 'onderhoud',      icon: Wrench,        color: '#B4FF39',
    label: 'Onderhoud',
    title: 'Onderhoud', message: 'De server wordt bijgewerkt. Even geduld.' },
  { key: 'buiten_dienst',  icon: Coffee,        color: '#7BCFFF',
    label: 'Even buiten dienst',
    title: 'Even buiten dienst', message: 'We zijn zo terug.' },
  { key: 'lan_gepauzeerd', icon: PauseCircle,   color: '#FFB57A',
    label: 'LAN gepauzeerd',
    title: 'LAN gepauzeerd', message: 'De LAN pauzeert even. Kom zo terug.' },
  { key: 'beperkt',        icon: AlertTriangle, color: '#FF7A6C',
    label: 'Beperkt beschikbaar',
    title: 'Beperkt beschikbaar', message: 'Sommige functies zijn tijdelijk offline.' },
]

export default function MaintenanceAdminPage() {
  usePageTitle('Onderhouds-modus — beheer')
  const { user } = useAuth()
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin'

  const [state, setState] = useState(null)
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState(null)
  const [form, setForm] = useState({
    is_active: false, preset: 'onderhoud', title: '', message: '', eta_at: '',
  })

  useEffect(() => {
    if (!isAdmin) return
    maintenanceAPI.get()
      .then(r => {
        setState(r.data)
        setForm({
          is_active: !!r.data.is_active,
          preset:    r.data.preset || 'onderhoud',
          title:     r.data.title || '',
          message:   r.data.message || '',
          eta_at:    r.data.eta_at ? new Date(r.data.eta_at).toISOString().slice(0,16) : '',
        })
      })
      .catch(e => setErr(e.response?.data?.error || e.message))
  }, [isAdmin])

  const kiesPreset = (key) => {
    const p = PRESETS.find(x => x.key === key)
    if (!p) return
    setForm(f => ({
      ...f,
      preset: key,
      // Alleen title/message overschrijven als velden leeg zijn OF als de
      // huidige waarden matchen met een bekende preset — anders behouden
      // we wat de admin al zelf getypt heeft.
      title:   presetMatches(f, 'title')   ? p.title   : f.title,
      message: presetMatches(f, 'message') ? p.message : f.message,
    }))
  }

  const bewaar = async (overrideActive) => {
    setSaving(true); setErr(null)
    try {
      const payload = {
        ...form,
        is_active: typeof overrideActive === 'boolean' ? overrideActive : form.is_active,
        eta_at: form.eta_at ? new Date(form.eta_at).toISOString() : null,
      }
      await maintenanceAPI.adminSet(payload)
      setForm(payload)
      setState(payload)
    } catch (e) {
      setErr(e.response?.data?.error || e.message)
    } finally { setSaving(false) }
  }

  if (!isAdmin) return <div className="p-6 text-muted-foreground">Enkel voor admins.</div>
  if (!state) return <div className="p-6 text-muted-foreground">Laden…</div>

  const activePreset = PRESETS.find(p => p.key === form.preset) || PRESETS[0]
  const Icon = activePreset.icon

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <PageHeader
        icon={Wrench}
        eyebrow="Beheer"
        title="Onderhouds-modus"
        subtitle="Full-screen overlay voor deelnemers. Admins blijven onbeperkt werken."
        actions={
          form.is_active
            ? <Button variant="outline" onClick={() => bewaar(false)} disabled={saving}>
                <Power className="h-4 w-4 mr-1.5" /> Uitschakelen
              </Button>
            : <Button onClick={() => bewaar(true)} disabled={saving}
                     className="bg-signal hover:bg-signal/90 text-background">
                <Power className="h-4 w-4 mr-1.5" /> Nu inschakelen
              </Button>
        }
      />

      {err && <div className="text-signal text-sm">{err}</div>}

      {/* Status-banner */}
      <div className="rounded border p-3 flex items-center gap-3 text-sm"
           style={{
             background:  form.is_active ? `${activePreset.color}15` : 'transparent',
             borderColor: form.is_active ? `${activePreset.color}55` : 'var(--border)',
             color:       form.is_active ? activePreset.color : undefined,
           }}>
        <Icon className="h-4 w-4" />
        <span className="font-mono uppercase tracking-widest text-xs">
          {form.is_active ? 'ACTIEF voor deelnemers' : 'Uit — alles normaal'}
        </span>
      </div>

      {/* Preset-keuze */}
      <Card>
        <CardContent className="p-4 space-y-3">
          <Label className="text-xs font-mono uppercase tracking-widest text-muted-foreground">
            Preset
          </Label>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {PRESETS.map(p => {
              const PIcon = p.icon
              const selected = form.preset === p.key
              return (
                <button key={p.key} type="button" onClick={() => kiesPreset(p.key)}
                  className={`flex flex-col items-center gap-1 p-3 rounded border-2 transition-colors text-xs ${
                    selected ? 'border-acid' : 'border-border hover:border-acid/50'
                  }`}
                  style={selected ? { background: `${p.color}12` } : {}}>
                  <PIcon className="h-5 w-5" style={{ color: p.color }} />
                  <span>{p.label}</span>
                </button>
              )
            })}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
            <div>
              <Label>Titel</Label>
              <Input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                     placeholder={activePreset.title} />
            </div>
            <div>
              <Label>Terug om <span className="text-muted-foreground text-xs">(optioneel)</span></Label>
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-muted-foreground shrink-0" />
                <Input type="datetime-local" value={form.eta_at}
                       onChange={e => setForm(f => ({ ...f, eta_at: e.target.value }))} />
              </div>
            </div>
          </div>

          <div>
            <Label>Boodschap</Label>
            <textarea rows={3} value={form.message}
              onChange={e => setForm(f => ({ ...f, message: e.target.value }))}
              placeholder={activePreset.message}
              className="w-full bg-card border border-border rounded px-3 py-2 text-sm outline-none focus:border-acid" />
          </div>
        </CardContent>
      </Card>

      {/* Preview */}
      <div>
        <Label className="text-xs font-mono uppercase tracking-widest text-muted-foreground">
          Voorbeeld voor deelnemers
        </Label>
        <div className="mt-2 rounded-lg border border-border bg-background p-8 text-center space-y-4"
             style={{ borderTop: `4px solid ${activePreset.color}` }}>
          <div className="mx-auto w-14 h-14 rounded-full grid place-items-center"
               style={{ background: `${activePreset.color}20`, color: activePreset.color }}>
            <Icon className="h-7 w-7" />
          </div>
          <div>
            <h2 className="font-[Rajdhani] font-bold text-2xl">{form.title || activePreset.title}</h2>
            <p className="text-muted-foreground mt-2 whitespace-pre-wrap">
              {form.message || activePreset.message}
            </p>
          </div>
          {form.eta_at && (
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded border border-border text-xs font-mono">
              <Clock className="h-3.5 w-3.5" />
              Verwacht terug: {new Date(form.eta_at).toLocaleString('nl-BE', { dateStyle: 'short', timeStyle: 'short' })}
            </div>
          )}
        </div>
      </div>

      <div className="flex justify-end">
        <Button onClick={() => bewaar()} disabled={saving}>
          <Save className="h-4 w-4 mr-1.5" /> {saving ? 'Bezig…' : 'Bewaren'}
        </Button>
      </div>
    </div>
  )
}

// Hulpfunctie: gebruikt de admin nu een van de bekende preset-teksten?
// Bij preset-wissel overschrijven we dan de tekst; anders behouden we z'n
// eigen custom tekst.
function presetMatches(form, field) {
  if (!form[field]) return true
  return PRESETS.some(p => p[field] === form[field])
}
